import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import Chart from 'react-apexcharts';

function clampPercentage(value) {
  return Math.max(0, Math.min(100, Number(value || 0)));
}

function ClientsActivationOverview({ response, status }) {
  const loading = status === 'loading';
  const error = status === 'error';
  const summary = response?.summary || {};
  const active = summary.active_clients || {};
  const returning = summary.returning_clients || {};
  const activeShare = clampPercentage(active.share_of_total);
  const returningShare = clampPercentage(returning.share_of_active);
  const periodLabel = response?.period?.label || 'Selected period';
  const countyLabel = response?.filters?.county?.label || 'All counties';

  const options = {
    chart: { type: 'radialBar', fontFamily: 'inherit' },
    colors: ['#00897b', '#7c3aed'],
    labels: ['Active / all clients', '2+ orders / active clients'],
    legend: { show: false },
    stroke: { lineCap: 'round' },
    plotOptions: {
      radialBar: {
        startAngle: -135,
        endAngle: 135,
        hollow: { size: '34%' },
        track: { background: '#e2e8f0', strokeWidth: '96%' },
        dataLabels: {
          name: {
            show: true,
            color: '#64748b',
            fontSize: '0.72rem',
            offsetY: -8,
          },
          value: {
            show: true,
            fontSize: '1.35rem',
            fontWeight: 800,
            color: '#111827',
            offsetY: 6,
            formatter: (value) => `${Number(value || 0).toFixed(1)}%`,
          },
          total: { show: false },
        },
      },
    },
    tooltip: {
      enabled: true,
      y: { formatter: (value) => `${Number(value || 0).toFixed(1)}%` },
    },
  };

  return (
    <Card
      component="section"
      aria-labelledby="activation-overview-heading"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        minHeight: 390,
        borderRadius: { xs: '14px', sm: '16px' },
        border: '1px solid rgba(15, 23, 42, 0.06)',
        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
        overflow: 'hidden',
      }}
    >
      {loading && response && <LinearProgress aria-label="Refreshing activation overview" />}
      <CardContent
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          minHeight: 0,
          p: '0 !important',
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1.25, sm: 1.5 },
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          }}
        >
          <Typography id="activation-overview-heading" sx={{ color: '#111827', fontWeight: 800, fontSize: '1.05rem' }}>
            Activation overview
          </Typography>
          <Typography sx={{ mt: 0.2, color: '#64748b', fontSize: '0.8rem' }}>
            Active clients and clients with 2+ orders · {periodLabel} · {countyLabel}
          </Typography>
        </Box>

        {error && !response ? (
          <Alert severity="error" sx={{ m: 1.5 }}>
            Activation data could not be loaded.
          </Alert>
        ) : loading && !response ? (
          <Box sx={{ display: 'grid', flex: 1, placeItems: 'center', p: 1.5 }}>
            <Skeleton
              variant="circular"
              sx={{ width: { xs: 210, sm: 230 }, height: { xs: 210, sm: 230 } }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              justifyContent: 'center',
              minHeight: 0,
              px: { xs: 1.25, sm: 2 },
              py: { xs: 0.75, sm: 1 },
            }}
          >
            <Box
              role="img"
              aria-label={`Activation overview for ${countyLabel}: active clients ${activeShare.toFixed(1)} percent of all clients; clients with at least 2 orders ${returningShare.toFixed(1)} percent of active clients`}
              sx={{
                alignSelf: 'center',
                width: '100%',
                maxWidth: { xs: 270, sm: 310, lg: 280, xl: 310 },
                height: { xs: 225, sm: 250, lg: 230, xl: 250 },
                minWidth: 0,
              }}
            >
              <Chart
                type="radialBar"
                width="100%"
                height="100%"
                options={options}
                series={[activeShare, returningShare]}
              />
            </Box>
            <Stack
              spacing={0.8}
              sx={{
                alignSelf: 'center',
                width: '100%',
                maxWidth: 420,
                mt: { xs: -0.25, sm: -0.75 },
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto',
                  alignItems: 'start',
                  columnGap: 1,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.7} minWidth={0}>
                  <Box sx={{ width: 10, height: 10, borderRadius: 999, bgcolor: '#00897b', flexShrink: 0 }} />
                  <Typography sx={{ color: '#334155', fontSize: '0.76rem', fontWeight: 650, lineHeight: 1.35 }}>
                    Active clients / all clients
                  </Typography>
                </Stack>
                <Typography sx={{ color: '#111827', fontSize: '0.78rem', fontWeight: 800, lineHeight: 1.35, whiteSpace: 'nowrap' }}>
                  {activeShare.toFixed(1)}% · {Number(active.value || 0).toLocaleString('ro-RO')}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto',
                  alignItems: 'start',
                  columnGap: 1,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.7} minWidth={0}>
                  <Box sx={{ width: 10, height: 10, borderRadius: 999, bgcolor: '#7c3aed', flexShrink: 0 }} />
                  <Typography sx={{ color: '#334155', fontSize: '0.76rem', fontWeight: 650, lineHeight: 1.35 }}>
                    Clients with 2+ orders / active clients
                  </Typography>
                </Stack>
                <Typography sx={{ color: '#111827', fontSize: '0.78rem', fontWeight: 800, lineHeight: 1.35, whiteSpace: 'nowrap' }}>
                  {returningShare.toFixed(1)}% · {Number(returning.value || 0).toLocaleString('ro-RO')}
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default ClientsActivationOverview;
